import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';

import { Calculator, Pencil, Trash2 } from 'lucide-react';

import { ConfirmDialog } from '@/ui/ConfirmDialog';
import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { EmptyState } from '@/ui/EmptyState';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { MoneyDisplay } from '@/ui/MoneyDisplay';
import { MoneyInput } from '@/ui/MoneyInput';
import { ResponsiveModal } from '@/ui/ResponsiveModal';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';

import { useCenarioDeTela } from '../_dev/cenarioDeTela';
import { EstadoSimulado } from '../_dev/EstadoSimulado';
import DADOS_SIMULADOS from './dados-simulados.json';
import {
  estaVigente,
  formatarDataBr,
  formatarPercentual,
  gerarIdLocal,
  type DadosTabelaDePrecos,
  type FaixaIr,
} from './tipos';

/**
 * Aba "Faixas Progressivas do IR": faixas de imposto de renda, agrupadas por
 * vigência — cada grupo é uma tabela progressiva completa (limites, alíquota
 * e parcela a deduzir), usada na apuração da retenção do transportador.
 *
 * Dado simulado por decisão de produto (ver `../_dev/cenarioDeTela.ts`): a
 * API do módulo financeiro ainda não existe. O CRUD abaixo é só em memória.
 */

const DADOS = DADOS_SIMULADOS as unknown as DadosTabelaDePrecos;

function itemVazio(): FaixaIr {
  return {
    id: '',
    vigenciaInicio: new Date().toISOString().slice(0, 10),
    vigenciaFim: null,
    ordem: 1,
    limiteInferior: '0',
    limiteSuperior: null,
    aliquota: 0,
    parcelaDeduzir: '0',
    observacoes: '',
  };
}

export interface FaixasIrTabHandle {
  abrirCriacao: () => void;
}

export const FaixasIrTab = forwardRef<FaixasIrTabHandle>((_props, ref) => {
  const cenario = useCenarioDeTela();
  const [itens, setItens] = useState<FaixaIr[]>([]);

  useEffect(() => {
    setItens(cenario === 'sucesso' ? DADOS.faixasIr : []);
  }, [cenario]);

  const [filtro, setFiltro] = useState<Record<string, string>>({});
  const [modalAberto, setModalAberto] = useState(false);
  const [editando, setEditando] = useState<FaixaIr | null>(null);
  const [form, setForm] = useState<FaixaIr>(itemVazio());

  const abrirCriacao = () => {
    setEditando(null);
    setForm(itemVazio());
    setModalAberto(true);
  };

  const abrirEdicao = (item: FaixaIr) => {
    setEditando(item);
    setForm(item);
    setModalAberto(true);
  };

  useImperativeHandle(ref, () => ({ abrirCriacao }));

  const salvar = () => {
    if (editando) {
      setItens((atual) =>
        atual.map((i) => (i.id === editando.id ? { ...form, id: editando.id } : i)),
      );
    } else {
      setItens((atual) => [...atual, { ...form, id: gerarIdLocal() }]);
    }
    setModalAberto(false);
  };

  const excluir = (id: string) => {
    setItens((atual) => atual.filter((i) => i.id !== id));
  };

  // Agrupa por vigência: cada intervalo (início|fim) vira uma tabela progressiva.
  const grupos = useMemo(() => {
    const mapa = new Map<string, FaixaIr[]>();
    for (const item of itens) {
      const chave = `${item.vigenciaInicio}|${item.vigenciaFim ?? ''}`;
      const lista = mapa.get(chave) ?? [];
      lista.push(item);
      mapa.set(chave, lista);
    }
    return Array.from(mapa.entries())
      .map(([chave, faixas]) => {
        const [inicioParte, fimParte] = chave.split('|');
        const inicio = inicioParte ?? '';
        const fim = fimParte || null;
        return { chave, inicio, fim, faixas: [...faixas].sort((a, b) => a.ordem - b.ordem) };
      })
      .sort((a, b) => b.inicio.localeCompare(a.inicio));
  }, [itens]);

  const opcoesVigencia = useMemo(
    () =>
      grupos.map((g) => ({
        value: g.chave,
        label: `${formatarDataBr(g.inicio)} — ${g.fim ? formatarDataBr(g.fim) : 'atual'}`,
      })),
    [grupos],
  );

  const gruposFiltrados = useMemo(() => {
    return grupos.filter((g) => {
      if (filtro.vigencia && g.chave !== filtro.vigencia) return false;
      const vigente = estaVigente(g.inicio, g.fim);
      if (filtro.status === 'VIGENTE' && !vigente) return false;
      if (filtro.status === 'ENCERRADA' && vigente) return false;
      return true;
    });
  }, [grupos, filtro]);

  const temFiltro = !!filtro.vigencia || !!filtro.status;

  const camposFiltro: FilterFieldConfig[] = [
    {
      key: 'vigencia',
      label: 'Vigência',
      type: 'select',
      placeholder: 'Todas',
      width: 'w-full sm:w-[260px]',
      options: opcoesVigencia,
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      placeholder: 'Todos',
      width: 'w-full sm:w-[160px]',
      options: [
        { value: 'VIGENTE', label: 'Vigente' },
        { value: 'ENCERRADA', label: 'Encerrada' },
      ],
    },
  ];

  const colunasFaixa: DataTableColumn<FaixaIr>[] = [
    {
      header: 'Ordem',
      accessorKey: 'ordem',
      headerClassName: 'w-[72px]',
      cellClassName: 'font-medium',
    },
    {
      header: 'Limite inferior',
      headerClassName: 'text-right',
      cellClassName: 'text-right',
      cell: (f) => <MoneyDisplay value={f.limiteInferior} />,
    },
    {
      header: 'Limite superior',
      headerClassName: 'text-right',
      cellClassName: 'text-right',
      cell: (f) => (f.limiteSuperior ? <MoneyDisplay value={f.limiteSuperior} /> : <span>—</span>),
    },
    {
      header: 'Alíquota',
      headerClassName: 'text-right',
      cellClassName: 'text-right font-mono text-sm',
      cell: (f) => formatarPercentual(f.aliquota),
    },
    {
      header: 'Parcela a deduzir',
      headerClassName: 'text-right',
      cellClassName: 'text-right',
      cell: (f) => <MoneyDisplay value={f.parcelaDeduzir} />,
    },
    {
      header: 'Observações',
      cellClassName: 'text-sm text-muted-foreground',
      cell: (f) => f.observacoes || '—',
    },
    {
      header: 'Ações',
      headerClassName: 'w-[96px]',
      cell: (f) => (
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => abrirEdicao(f)}
            aria-label={`Editar faixa ${f.ordem}`}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <ConfirmDialog
            trigger={
              <Button variant="ghost" size="icon" aria-label={`Excluir faixa ${f.ordem}`}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            }
            title="Excluir faixa"
            description="Esta faixa será removida da tabela progressiva."
            confirmLabel="Excluir"
            variant="destructive"
            onConfirm={() => excluir(f.id)}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <FilterBar
        fields={camposFiltro}
        values={filtro}
        onChange={(chave, valor) => setFiltro((atual) => ({ ...atual, [chave]: valor }))}
        onClear={() => setFiltro({})}
        hasActiveFilters={temFiltro}
      />

      <EstadoSimulado
        cenario={cenario}
        descricao="as faixas progressivas do IR"
        colunasDoEsqueleto={7}
      >
        {gruposFiltrados.length === 0 ? (
          <Card>
            <CardContent className="p-0">
              <EmptyState
                icon={Calculator}
                title={temFiltro ? 'Nenhum resultado' : 'Nenhuma faixa cadastrada'}
                description={
                  temFiltro
                    ? 'Ajuste os filtros para ver outras vigências.'
                    : 'Cadastre as faixas progressivas do IR.'
                }
                actionLabel={temFiltro ? undefined : 'Nova faixa'}
                onAction={temFiltro ? undefined : abrirCriacao}
              />
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {gruposFiltrados.map((grupo) => (
              <Card key={grupo.chave}>
                <CardHeader>
                  <CardTitle className="text-sm">
                    Vigência: {formatarDataBr(grupo.inicio)} —{' '}
                    {grupo.fim ? formatarDataBr(grupo.fim) : 'atual'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <DataTable
                    columns={colunasFaixa}
                    data={grupo.faixas}
                    showPagination={false}
                    ariaLabel={`Faixas de IR — vigência iniciada em ${formatarDataBr(grupo.inicio)}`}
                    emptyIcon={Calculator}
                    emptyTitle="Sem faixas nesta vigência"
                  />
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </EstadoSimulado>

      <ResponsiveModal
        open={modalAberto}
        onOpenChange={setModalAberto}
        title={editando ? 'Editar faixa de IR' : 'Nova faixa de IR'}
        footer={
          <>
            <Button variant="outline" onClick={() => setModalAberto(false)}>
              Cancelar
            </Button>
            <Button onClick={salvar}>Salvar</Button>
          </>
        }
      >
        <div className="grid gap-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="ir-inicio">Vigência início</Label>
              <Input
                id="ir-inicio"
                type="date"
                value={form.vigenciaInicio}
                onChange={(e) => setForm({ ...form, vigenciaInicio: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="ir-fim">Vigência fim</Label>
              <Input
                id="ir-fim"
                type="date"
                value={form.vigenciaFim ?? ''}
                onChange={(e) => setForm({ ...form, vigenciaFim: e.target.value || null })}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="grid gap-2">
              <Label htmlFor="ir-ordem">Ordem</Label>
              <Input
                id="ir-ordem"
                type="number"
                min="1"
                value={form.ordem}
                onChange={(e) => setForm({ ...form, ordem: parseInt(e.target.value, 10) || 1 })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="ir-liminf">Limite inferior</Label>
              <MoneyInput
                id="ir-liminf"
                value={form.limiteInferior}
                onChange={(v) => setForm({ ...form, limiteInferior: v })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="ir-limsup">Limite superior</Label>
              <MoneyInput
                id="ir-limsup"
                value={form.limiteSuperior ?? ''}
                onChange={(v) => setForm({ ...form, limiteSuperior: v || null })}
                placeholder="Sem limite"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="ir-aliquota">Alíquota</Label>
              <Input
                id="ir-aliquota"
                type="number"
                step="0.001"
                min="0"
                value={form.aliquota}
                onChange={(e) => setForm({ ...form, aliquota: Number(e.target.value) || 0 })}
              />
              <span className="text-xs text-muted-foreground">
                {formatarPercentual(form.aliquota)}
              </span>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="ir-parcela">Parcela a deduzir</Label>
              <MoneyInput
                id="ir-parcela"
                value={form.parcelaDeduzir}
                onChange={(v) => setForm({ ...form, parcelaDeduzir: v })}
              />
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="ir-obs">Observações</Label>
            <Input
              id="ir-obs"
              value={form.observacoes}
              onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
              placeholder="Ex.: Isento"
            />
          </div>
        </div>
      </ResponsiveModal>
    </div>
  );
});

FaixasIrTab.displayName = 'FaixasIrTab';
